import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_PAGE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

interface CharactersAnalysis {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  documentTitle?: string;
  createdAt: string;
}

export const CharactersAnalyses: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [charactersAnalyses, setCharactersAnalyses] = useState<CharactersAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCharactersAnalyses = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/characters-analyses?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/characters-analyses`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch characters analyses');
        }

        const data = await response.json();
        setCharactersAnalyses(data.charactersAnalyses || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching characters analyses:', err);
        setError(t('failed_to_load_characters_analyses'));
      } finally {
        setLoading(false);
      }
    };

    fetchCharactersAnalyses();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const totalCount = charactersAnalyses.length;

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>{t('total')}</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{totalCount}</div>
          </div>
        </div>

        {charactersAnalyses.length === 0 ? (
          <AdminEmptyState>{t('no_characters_analyses_registered')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[1000px]">
              <thead>
                <tr>
                  <th>
                    {t('user')}
                  </th>
                  <th>
                    {t('document')}
                  </th>
                  <th>
                    {t('document_title')}
                  </th>
                  <th>
                    {t('date')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {charactersAnalyses.map((ca) => (
                  <tr
                    key={ca.id}
                  >
                    <td>
                      {ca.userEmail ? (
                        <Link
                          to={`/admin/users/${ca.userId}`}
                          className="admin-table-link"
                        >
                          {ca.userEmail}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {ca.documentId ? (
                        <Link
                          to={`/documents/${ca.documentId}`}
                          className="admin-table-link"
                        >
                          {ca.documentId.substring(0, 8)}...
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="max-w-[300px]">
                      <div className="truncate" title={ca.documentTitle || '-'}>
                        {ca.documentTitle || '-'}
                      </div>
                    </td>
                    <td className="admin-table-cell-muted">
                      {new Date(ca.createdAt).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}
      </div>
    </>
  );
};
